import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import Button from "../../components/Buttons/Button";
import api from "../../services/api";
import { PUSH_LIMITS, charCount, countState } from "../../utils/pushLimits";
import "./PushSchedule.scss";

// Fallbacks match the server caps; the list response's `limits` wins when present.
const DEFAULT_LIMITS = PUSH_LIMITS;
const TZ = "America/New_York";

const etParts = (ms) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date(ms));
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
};

// epoch ms -> "yyyy-MM-ddTHH:mm" as Eastern wall-clock time, for datetime-local.
const toEasternInput = (ms) => {
  if (!ms) return "";
  const p = etParts(Number(ms));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

// Eastern wall-clock "yyyy-MM-ddTHH:mm" -> epoch ms, DST-aware.
const fromEasternInput = (str) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(str || "");
  if (!m) return null;
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  const offsetAt = (t) => {
    const p = etParts(t);
    return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - t;
  };
  let t = wall - offsetAt(wall);
  t = wall - offsetAt(t);
  return t;
};

const formatEastern = (ms) =>
  ms
    ? `${new Intl.DateTimeFormat("en-US", {
        timeZone: TZ, weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      }).format(new Date(Number(ms)))} ET`
    : "Not set";

const STATUS_LABEL = {
  draft: "Draft",
  pending: "Scheduled",
  sending: "Sending",
  sent: "Sent",
  failed: "Failed",
};

const audienceText = (p) => {
  const base = "Everyone with notifications on";
  const target = p.event_id ? `, opens event ${p.event_id}` : p.link ? `, opens ${p.link}` : "";
  const reached = p.status === "sent" && p.audience != null ? ` (reached ${p.sent ?? 0} of ${p.audience})` : "";
  return base + target + reached;
};

// True when the approved copy is not what was first proposed.
const copyChanged = (p) =>
  p.proposed_message != null &&
  ((p.proposed_title || "") !== (p.title || "") || p.proposed_message !== (p.message || ""));

const proposedText = (p) =>
  p.proposed_title ? `${p.proposed_title} / ${p.proposed_message}` : p.proposed_message;

// "Approved by X, date" plus the original proposal in gray when it was rewritten.
const ApprovalNote = ({ push }) => {
  if (!push.approved_at) return null;
  return (
    <div className="push-schedule__approval">
      <div className="push-schedule__approved">
        Approved by {push.approved_by_name || "an admin"}, {formatEastern(push.approved_at)}
      </div>
      {copyChanged(push) && (
        <div className="push-schedule__proposed">Proposed: {proposedText(push)}</div>
      )}
    </div>
  );
};

const Counter = ({ value, max, preferred }) => {
  const n = charCount(value);
  const state = countState(n, max, preferred);
  return (
    <span className={`push-schedule__count ${state}`.trim()}>
      {n}/{max}{state === "is-long" ? ` · ${preferred} or fewer is best` : ""}
    </span>
  );
};

const UpcomingRow = ({ push, limits, onChanged }) => {
  const [title, setTitle] = useState(push.title || "");
  const [message, setMessage] = useState(push.message || "");
  const [when, setWhen] = useState(toEasternInput(push.scheduled_for));
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    setTitle(push.title || "");
    setMessage(push.message || "");
    setWhen(toEasternInput(push.scheduled_for));
    // Keyed on this row's own fields so reloading the list keeps other rows' unsaved edits.
  }, [push._id, push.title, push.message, push.scheduled_for]);

  const editable = push.status === "draft" || push.status === "pending";
  const titleOver = charCount(title) > limits.title;
  const messageOver = charCount(message) > limits.message;
  const messageEmpty = !message.trim();
  const whenMs = fromEasternInput(when);
  const dirty =
    title !== (push.title || "") ||
    message !== (push.message || "") ||
    when !== toEasternInput(push.scheduled_for);
  const canSave = editable && dirty && !titleOver && !messageOver && !messageEmpty && !!whenMs;

  const run = async (kind, fn, okText) => {
    setBusy(kind);
    try {
      await fn();
      toast.success(okText);
      onChanged();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setBusy(null);
    }
  };

  const save = () =>
    run(
      "save",
      () =>
        api.patch(`/push-notification-admin/scheduled/${push._id}`, {
          title: title.trim(),
          message: message.trim(),
          scheduled_for: whenMs,
        }),
      "Saved"
    );

  const approve = () => {
    if (dirty) {
      toast.warn("Save your changes before approving");
      return;
    }
    run(
      "approve",
      () => api.post(`/push-notification-admin/scheduled/${push._id}/approve`),
      `Approved for ${formatEastern(push.scheduled_for)}`
    );
  };

  const cancel = () => {
    if (!window.confirm("Cancel this push? It won't be sent.")) return;
    run("cancel", () => api.delete(`/push-notification-admin/scheduled/${push._id}`), "Canceled");
  };

  return (
    <li className="push-schedule__row">
      <div className="push-schedule__row-head">
        <span className="push-schedule__when">{formatEastern(push.scheduled_for)}</span>
        <span className={`push-schedule__status is-${push.status}`}>
          {STATUS_LABEL[push.status] || push.status}
        </span>
      </div>

      <div className="push-schedule__fields">
        <label className="push-schedule__field">
          <span className="push-schedule__label">
            Title <Counter value={title} max={limits.title} preferred={limits.titlePreferred} />
          </span>
          <input type="text" value={title} disabled={!editable} onChange={(e) => setTitle(e.target.value)} />
        </label>

        <label className="push-schedule__field">
          <span className="push-schedule__label">
            Message <Counter value={message} max={limits.message} />
          </span>
          <textarea rows={3} value={message} disabled={!editable} onChange={(e) => setMessage(e.target.value)} />
        </label>

        <label className="push-schedule__field push-schedule__field--time">
          <span className="push-schedule__label">Send time (Eastern)</span>
          <input type="datetime-local" value={when} disabled={!editable} onChange={(e) => setWhen(e.target.value)} />
        </label>

        <div className="push-schedule__audience">Audience: {audienceText(push)}</div>
        <ApprovalNote push={push} />
      </div>

      {editable && (
        <div className="push-schedule__actions">
          <Button
            type="button"
            gradientButton
            textColor="#FFFFFF"
            onClick={save}
            disabled={!canSave || !!busy}
            showLoading={busy === "save"}
          >
            Save
          </Button>
          {push.status === "draft" && (
            <Button
              type="button"
              className="btn-outline-light"
              onClick={approve}
              disabled={!!busy || titleOver || messageOver || messageEmpty || !whenMs || whenMs <= Date.now()}
              showLoading={busy === "approve"}
            >
              Approve
            </Button>
          )}
          <Button
            type="button"
            className="btn-outline-light"
            onClick={cancel}
            disabled={!!busy}
            showLoading={busy === "cancel"}
          >
            Cancel
          </Button>
          {(titleOver || messageOver) && (
            <span className="push-schedule__over-note">Shorten the red field to save.</span>
          )}
        </div>
      )}
    </li>
  );
};

// Read-only log of final copy next to what was proposed, to learn the voice from.
const ApprovedCopy = ({ pushes, loading }) => (
  <>
    <h3 className="push-schedule__section-title">Approved copy ({pushes.length})</h3>
    {loading ? (
      <p className="push-schedule__empty">Loading...</p>
    ) : pushes.length === 0 ? (
      <p className="push-schedule__empty">Nothing approved yet.</p>
    ) : (
      <ul className="push-schedule__list">
        {pushes.map((p) => (
          <li key={p._id} className="push-schedule__row is-readonly">
            <div className="push-schedule__row-head">
              <span className="push-schedule__when">{formatEastern(p.sent_at || p.scheduled_for)}</span>
              <span className={`push-schedule__status is-${p.status}`}>
                {STATUS_LABEL[p.status] || p.status}
              </span>
            </div>
            {p.title && <div className="push-schedule__sent-title">{p.title}</div>}
            <div className="push-schedule__sent-msg">{p.message}</div>
            {copyChanged(p) && (
              <div className="push-schedule__proposed">Proposed: {proposedText(p)}</div>
            )}
            <div className="push-schedule__audience">
              {[
                p.proposed_by_name && `Proposed by ${p.proposed_by_name}`,
                p.approved_at && `Approved by ${p.approved_by_name || "an admin"}, ${formatEastern(p.approved_at)}`,
                p.status === "sent" && p.audience != null && `Reached ${p.sent ?? 0} of ${p.audience}`,
              ]
                .filter(Boolean)
                .join(". ")}
            </div>
          </li>
        ))}
      </ul>
    )}
  </>
);

const PushSchedule = () => {
  const [upcoming, setUpcoming] = useState([]);
  const [sent, setSent] = useState([]);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("schedule");

  const load = useCallback(async () => {
    try {
      const [up, done] = await Promise.all([
        api.get(`/push-notification-admin/scheduled`, { params: { status: "draft,pending,sending" } }),
        api.get(`/push-notification-admin/scheduled`, { params: { status: "sent,failed" } }),
      ]);
      const byTime = (a, b) => (a.scheduled_for || 0) - (b.scheduled_for || 0);
      setUpcoming((up.data.data || []).slice().sort(byTime));
      setSent((done.data.data || []).slice().sort((a, b) => byTime(b, a)));
      if (up.data.limits) setLimits({ ...DEFAULT_LIMITS, ...up.data.limits });
    } catch (error) {
      console.error("Error loading push schedule:", error);
      toast.error("Couldn't load the push schedule");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Everything an admin signed off on (scheduled or sent), newest first.
  const approvedCopy = [...upcoming, ...sent]
    .filter((p) => ["pending", "sending", "sent"].includes(p.status))
    .sort((a, b) => (b.scheduled_for || 0) - (a.scheduled_for || 0));

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <section className="push-schedule">
            <h2 className="page-title m-0">Push Schedule</h2>
            <p className="push-schedule__intro">
              Drafts don't send until you approve them. Title up to {limits.title} characters
              ({limits.titlePreferred} or fewer is best), message up to {limits.message}, so nothing gets cut off on the lock screen.
            </p>

            <div className="push-schedule__views">
              {[["schedule", "Schedule"], ["approved", "Approved copy"]].map(([key, label]) => (
                <Button
                  key={key}
                  type="button"
                  gradientButton={view === key}
                  textColor={view === key ? "#FFFFFF" : undefined}
                  className={view === key ? undefined : "btn-outline-light"}
                  onClick={() => setView(key)}
                >
                  {label}
                </Button>
              ))}
            </div>

            {view === "approved" ? (
              <ApprovedCopy pushes={approvedCopy} loading={loading} />
            ) : (
            <>
            <h3 className="push-schedule__section-title">Upcoming ({upcoming.length})</h3>
            {loading ? (
              <p className="push-schedule__empty">Loading...</p>
            ) : upcoming.length === 0 ? (
              <p className="push-schedule__empty">Nothing scheduled yet.</p>
            ) : (
              <ul className="push-schedule__list">
                {upcoming.map((p) => (
                  <UpcomingRow key={p._id} push={p} limits={limits} onChanged={load} />
                ))}
              </ul>
            )}

            <h3 className="push-schedule__section-title">Sent ({sent.length})</h3>
            {!loading && sent.length === 0 ? (
              <p className="push-schedule__empty">Nothing sent yet.</p>
            ) : (
              <ul className="push-schedule__list">
                {sent.map((p) => (
                  <li key={p._id} className="push-schedule__row is-readonly">
                    <div className="push-schedule__row-head">
                      <span className="push-schedule__when">{formatEastern(p.sent_at || p.scheduled_for)}</span>
                      <span className={`push-schedule__status is-${p.status}`}>
                        {STATUS_LABEL[p.status] || p.status}
                      </span>
                    </div>
                    {p.title && <div className="push-schedule__sent-title">{p.title}</div>}
                    <div className="push-schedule__sent-msg">{p.message}</div>
                    <div className="push-schedule__audience">Audience: {audienceText(p)}</div>
                    <ApprovalNote push={p} />
                    {p.status === "failed" && p.error && (
                      <div className="push-schedule__error">Error: {p.error}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            </>
            )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default PushSchedule;
